/**
 * Rndm by Grant Skinner. Jan 15, 2008
 * Visit www.gskinner.com/blog for documentation, updates and more free code.
 *
 *
 * Copyright (c) 2008 Grant Skinner
 *
 * Permission is hereby granted, free of charge, to any person
 * obtaining a copy of this software and associated documentation
 * files (the "Software"), to deal in the Software without
 * restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the
 * Software is furnished to do so, subject to the following
 * conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 * OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
 * OTHER DEALINGS IN THE SOFTWARE.
 */

import BitmapData = createjs.BitmapData;
var rand = require('random-seed').create();
// Provides common random functions using a seeded random system. Can be used through static interface or via instantiation.

export class SeededRandom {
    // static interface:
    // NOTE: for usage information, look at the instance methods below.

    protected static _instance: SeededRandom;

    public static get instance(): SeededRandom {
        if (SeededRandom._instance == null) {
            SeededRandom._instance = new SeededRandom();
        }
        return SeededRandom._instance;
    }

    public static get seed(): number {
        return rand.seed;
    }

    public static set seed(value: number) {
        rand.seed(value);
    }

    public static get pointer(): number {
        return SeededRandom.instance.pointer;
    }

    public static set pointer(value: number) {
        SeededRandom.instance.pointer = value;
    }

    public static random(): number {
        return SeededRandom.instance.random();
    }

    public static float(min: number, max: number = NaN): number {
        return SeededRandom.instance.float(min, max);
    }

    public static boolean(chance: number = 0.5): boolean {
        return SeededRandom.instance.boolean(chance);
    }

    public static sign(chance: number = 0.5): number {
        return SeededRandom.instance.sign(chance);
    }

    public static bit(chance: number = 0.5): number {
        return SeededRandom.instance.bit(chance);
    }

    public static integer(min: number, max: number = NaN): number {
        return SeededRandom.instance.integer(min, max);
    }

    public static reset(): void {
    }

    // constants:
    // private properties:
    protected _seed: number = 0;
    protected _pointer: number = 0;
    protected bmpd: BitmapData;
    protected seedInvalid: boolean = true;

    // public properties:

    // constructor:
    constructor(seed: number = 0) {
        this._seed = seed;
        this.bmpd = new BitmapData(1000, 200);
    }

    // public getter/setters:

    // seed = Math.random()*0xFFFFFF; // sets a random seed
    // seed = 50; // sets a static seed
    public get seed(): number {
        return this._seed;
    }

    public set seed(value: number) {
        if (value != this._seed) {
            this.seedInvalid = true;
            this._pointer = 0;
        }
        this._seed = value;
    }

    // trace(Rndm.pointer); // traces the current position in the number series
    // Rndm.pointer = 50; // moves the pointer to the 50th number in the series
    public get pointer(): number {
        return this._pointer;
    }

    public set pointer(value: number) {
        this._pointer = value;
    }

    // public methods:
    // random(); // returns a number between 0-1 exclusive.
    public random(): number {
        return rand.random();
    }

    // float(50); // returns a number between 0-50 exclusive
    // float(20,50); // returns a number between 20-50 exclusive
    public float(min: number, max: number = NaN): number {
        if (isNaN(max)) {
            max = min;
            min = 0;
        }
        return rand.floatBetween(min, max);
    }

    // boolean(); // returns true or false (50% chance of true)
    // boolean(0.8); // returns true or false (80% chance of true)
    public boolean(chance: number = 0.5): boolean {
        return (SeededRandom.random() < chance);
    }

    // sign(); // returns 1 or -1 (50% chance of 1)
    // sign(0.8); // returns 1 or -1 (80% chance of 1)
    public sign(chance: number = 0.5): number {
        return (SeededRandom.random() < chance) ? 1 : -1;
    }

    // bit(); // returns 1 or 0 (50% chance of 1)
    // bit(0.8); // returns 1 or 0 (80% chance of 1)
    public bit(chance: number = 0.5): number {
        return (SeededRandom.random() < chance) ? 1 : 0;
    }

    // integer(50); // returns an integer between 0-49 inclusive
    // integer(20,50); // returns an integer between 20-49 inclusive
    public integer(min: number, max: number = NaN): number {
        if (isNaN(max)) {
            max = min;
            min = 0;
        }
        return rand.intBetween(min, max);
    }

    // reset(); // resets the number series, retaining the same seed
    public reset(): void {
        this._pointer = 0;
    }

    // private methods:
}
